// ClickHouseStatus.js — small traffic-light dot on the user avatar showing whether
// the ClickHouse data warehouse is awake. Green = awake, amber (spinning) = waking up,
// red = it tried to wake it and couldn't. Click to re-check or retry.
//
// The dot should be green essentially always. Three things keep it that way, and they cover
// different failure modes:
//   1. The backend runs a keep-alive query every few minutes (see backend/db.py), so the
//      service never idles out while the backend is up, and this check is answered from memory.
//   2. A scheduled ping keeps the backend itself from idling out (.github/workflows/keep-awake.yml).
//   3. If it does find the service asleep, this component wakes it *by itself* rather than
//      waiting to be clicked — nobody should have to know to click a red dot.
// Red therefore means a real problem, not merely "first visitor after lunch".
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8010';

async function checkOnce() {
  try {
    const r = await fetch(`${API_BASE}/dataentry/health/clickhouse`);
    if (!r.ok) return false;
    const d = await r.json();
    return !!d.awake;
  } catch (e) {
    return false;
  }
}

function ClickHouseStatus() {
  const [state, setState] = useState('checking'); // checking | awake | asleep | waking
  const stateRef = useRef(state);
  stateRef.current = state;
  const pollRef = useRef(null);
  const location = useLocation();

  // Declared before refresh so refresh can start a wake on its own; the ref keeps the two from
  // depending on each other's identity and re-creating the effects below on every render.
  const startWakeRef = useRef(null);

  const refresh = useCallback(async () => {
    if (stateRef.current === 'waking') return; // don't interrupt an active wake
    const awake = await checkOnce();
    if (awake) { setState('awake'); return; }
    // Found it asleep: wake it rather than sitting there red waiting for a click.
    if (startWakeRef.current) startWakeRef.current();
    else setState('asleep');
  }, []);

  const startWake = useCallback(() => {
    setState('waking');
    // Fire the real wake: the backend runs a small query and waits for the compute to
    // spin up (that's what actually wakes it). Its result flips the dot green too.
    fetch(`${API_BASE}/dataentry/health/clickhouse/wake`, { method: 'POST' })
      .then((r) => r.json())
      .then((d) => { if (d && d.awake) { clearInterval(pollRef.current); setState('awake'); } })
      .catch(() => {});
    // Poll health in parallel so the dot flips green the moment it's up.
    clearInterval(pollRef.current);
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      const awake = await checkOnce();
      if (awake) { clearInterval(pollRef.current); setState('awake'); }
      else if (tries >= 45) { clearInterval(pollRef.current); setState('asleep'); } // ~135s cap
    }, 3000);
  }, []);

  useEffect(() => { startWakeRef.current = startWake; }, [startWake]);

  // Re-check on initial app load AND on every page navigation — the backend answers this from
  // its keep-alive's last result, so it costs nothing and reflects the service's real state.
  // Throttled to one check per 30s while it is green: clicking through five pages in a minute
  // otherwise asks five times for an answer that cannot have changed.
  const lastCheck = useRef(0);
  useEffect(() => {
    const now = Date.now();
    if (stateRef.current === 'awake' && now - lastCheck.current < 30000) return;
    lastCheck.current = now;
    refresh();
  }, [location.pathname, refresh]);

  // Background keep-alive: periodic re-check + a re-check whenever the tab regains focus. A hidden
  // tab skips the tick — the focus re-check covers it the moment anyone looks — so a tab left open
  // all day is not a request a minute against the backend.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      lastCheck.current = Date.now();
      refresh();
    }, 60000);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); clearInterval(pollRef.current); document.removeEventListener('visibilitychange', onVis); };
  }, [refresh]);

  const onClick = (e) => {
    e.stopPropagation();
    if (state === 'waking') return;
    if (state === 'awake') { refresh(); return; }
    startWake();
  };

  const label = {
    checking: 'Checking ClickHouse…',
    awake: 'ClickHouse: awake',
    asleep: 'ClickHouse: asleep — click to wake it',
    waking: 'ClickHouse: waking up…',
  }[state];

  return (
    <button
      type="button"
      className={`ch-status ch-${state}`}
      onClick={onClick}
      title={label}
      aria-label={label}
    />
  );
}

export default ClickHouseStatus;
